import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import NetworkManager from './networking.js';
import AuthManager from './auth.js';
import mapData from './mapData.js';
import GrassShader from './shaders/GrassShader.js';
import WaterShader from './shaders/WaterShader.js';

class Game {
    constructor() {
        this.debug = false;
        this.mapData = mapData;
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.5, 2000);
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        
        // Player state
        this.player = {
            position: new THREE.Vector3(0, 0, 0),
            inWater: false,
            inventory: []
        };
        
        this.players = new Map();  // uuid -> model
        this.playerNames = new Map(); // uuid -> name
        this.fishingLines = new Map(); // uuid -> line
        this.chatBubbles = new Map(); // uuid -> {sprite, timestamp}

        // Auth and networking
        this.auth = new AuthManager();
        this.network = new NetworkManager();
        this.registerNetworkHandlers();
        this.playerModel = null;
        this.isMoving = false;
        this.moveSpeed = 0.5;
        this.maxMoveSpeed = 0.5;
        this.minMoveDistance = 0.1;
        this.island = null;
        this.pathPoints = [];
        this.inventory = [];
        this.isCasting = false;
        this.castingStartTime = 0;
        this.cameraAngle = 0;         // orbit angle in radians
        this.cameraZoom = 2.0;        // zoom level (1.0 to 3.0), start at mid zoom
        this.cameraVelocity = 0;      // drag inertia
        this.isFishing = false;
        this.targetPosition = null;
        this.lastPosition = null;
        this.stuckCounter = 0;
        this.maxStuckCount = 5;
        this.previousDirections = [];
        this.slowdownDistance = 1.0;
        this.islandSize = 650;
        this.currentMoveSeq = 0;    // sequence number of active move_to
        this.localMoveTicks = 0;    // client ticks simulated for current movement
        this._smoothTarget = null;  // {x, z} — smoothed reconciliation target to lerp toward
        this._cameraTarget = { x: 0, z: 0 }; // smoothed camera focus point (deadzone)

        // Fishing minigame state
        this.minigameActive = false;
        this.minigameFishPos = 0.5;
        this.minigameReelPos = 0.5;
        this.minigameOverlapTime = 0;
        this.minigameTimeLeft = 15;
        this.minigameFishDifficulty = 1;
        this.minigameFishDir = 1;
        this.minigameFishTimer = 0;
        this.minigameReelVelocity = 0;
        this._savedCameraState = null;
        
        // Initialize FPS counter
        this.fpsCounter = {
            element: null,
            frames: 0,
            lastTime: performance.now(),
            value: 0
        };
        
        // Time tracking for frame-independent movement
        this.clock = new THREE.Clock();
        this.lastTime = 0;
        this.deltaTime = 0;
        this.fixedTimeStep = 1/60; // Target 60 FPS for physics/movement updates
        this.timeAccumulator = 0;
        
        
        // Performance monitoring
        this.fpsHistory = [];
        this.fpsAverageWindow = 20; // Average over last 20 frames
        
        this.init();
    }

    init() {
        // Setup renderer with adaptive quality settings
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setClearColor(0x7ec8e3); // Bright tropical sky
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.15;
        
        // Create FPS counter (hidden by default)
        this.createFpsDisplay();
        
        // Create chat input UI
        this.createChatUI();
        document.getElementById('game-container').appendChild(this.renderer.domElement);

        // Setup camera
        this.updateCamera();

        // Handle window resize
        window.addEventListener('resize', () => {
            this.camera.aspect = window.innerWidth / window.innerHeight;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(window.innerWidth, window.innerHeight);
        });

        // Create heightmap texture for water shader
        const { gridSize, islandSize, heights } = this.mapData;
        const heightData = new Float32Array(gridSize * gridSize);
        for (let i = 0; i < heights.length; i++) {
            heightData[i] = heights[i];
        }
        const heightMapTex = new THREE.DataTexture(
            heightData, gridSize, gridSize, THREE.RedFormat, THREE.FloatType
        );
        heightMapTex.minFilter = THREE.LinearFilter;
        heightMapTex.magFilter = THREE.LinearFilter;
        heightMapTex.wrapS = THREE.ClampToEdgeWrapping;
        heightMapTex.wrapT = THREE.ClampToEdgeWrapping;
        heightMapTex.needsUpdate = true;

        // Create ocean with animated water shader
        const oceanSize = 2000;
        const oceanSegs = 128;
        const oceanGeometry = new THREE.CircleGeometry(oceanSize, oceanSegs);
        oceanGeometry.rotateX(-Math.PI / 2);

        this.waterUniforms = {
            time: { value: 0 },
            islandRadius: { value: this.islandSize / 2 },
            heightMap: { value: heightMapTex },
            mapSize: { value: islandSize }
        };
        const oceanMaterial = new THREE.ShaderMaterial({
            vertexShader: WaterShader.vertexShader,
            fragmentShader: WaterShader.fragmentShader,
            uniforms: this.waterUniforms,
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        this.ocean = new THREE.Mesh(oceanGeometry, oceanMaterial);
        this.ocean.position.y = -0.1;
        this.scene.add(this.ocean);

        // Bright tropical lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);

        // Warm hemisphere light for natural outdoor fill
        const hemiLight = new THREE.HemisphereLight(0x87ceeb, 0x4a8c38, 0.4);
        this.scene.add(hemiLight);

        // Main sun directional light
        const sunLight = new THREE.DirectionalLight(0xfffae6, 2.2);
        sunLight.position.set(80, 120, 60);
        sunLight.castShadow = true;
        sunLight.shadow.mapSize.width = 2048;
        sunLight.shadow.mapSize.height = 2048;
        sunLight.shadow.camera.near = 0.1;
        sunLight.shadow.camera.far = 1000;
        sunLight.shadow.camera.left = -200;
        sunLight.shadow.camera.right = 200;
        sunLight.shadow.camera.top = 200;
        sunLight.shadow.camera.bottom = -200;
        this.scene.add(sunLight);

        // Warm rim light to simulate sun reflection
        const rimLight = new THREE.DirectionalLight(0xffd500, 0.3);
        rimLight.position.set(-50, 20, 0);
        this.scene.add(rimLight);

        // Create a realistic island with height map
        this.createIsland();

        // Add instanced grass blades
        this.createGrass();

        // Add palm trees, rocks, bushes, and flowers
        this.addVegetation();

        // Create inventory UI
        this.createInventoryUI();

        // Check for existing session
        const savedPlayerName = this.auth.restoreLastName();
        if (savedPlayerName) {
            // Auto-fill the player name
            const nameInput = document.getElementById('player-name');
            nameInput.value = savedPlayerName;
        }

        // Setup login handlers
        document.getElementById('start-game').addEventListener('click', () => this.startGame());
        
        // Setup instruction screen handler
        document.getElementById('start-playing').addEventListener('click', () => {
            document.getElementById('intro-overlay').style.display = 'none';
            // Enable game controls after instructions are dismissed
            this.setupGameControls();
        });

        // Start animation loop
        this.animate();
    }

    createFpsDisplay() {
        // Reference the FPS counter element
        this.fpsCounter.element = document.getElementById('fps-counter');
    }
    
    updateFpsCounter() {
        // Update FPS calculation
        this.fpsCounter.frames++;
        const currentTime = performance.now();
        const elapsed = currentTime - this.fpsCounter.lastTime;
        
        if (elapsed >= 1000) {
            this.fpsCounter.value = Math.round((this.fpsCounter.frames * 1000) / elapsed);
            const ping = this.network.ping;
            const jitter = this.network.jitter;
            const interpDelay = this.network.interpDelay;
            if (this.debug) {
                this.fpsCounter.element.textContent = `FPS: ${this.fpsCounter.value} | Ping: ${ping}ms | Jitter: ${jitter}ms | Interp: ${Math.round(interpDelay)}ms`;
            } else {
                this.fpsCounter.element.textContent = `FPS: ${this.fpsCounter.value}`;
            }
            this.fpsCounter.frames = 0;
            this.fpsCounter.lastTime = currentTime;
        }
    }

    log(...args) {
        if (this.debug) {
            console.log(...args);
        }
    }

    addVegetation() {
        const gltfLoader = new GLTFLoader();
        const islandRadius = this.islandSize / 2;
        const basePath = '/assets/nature_props/gltf/';

        // Seeded random for deterministic placement
        let seed = 12345;
        const rand = () => {
            seed = (seed * 16807 + 0) % 2147483647;
            return (seed - 1) / 2147483646;
        };

        const loadModel = (file, x, z, h, scale, rotY, opts = {}) => {
            gltfLoader.load(basePath + file, (gltf) => {
                const model = gltf.scene;
                model.scale.setScalar(scale);
                model.position.set(x, h + (opts.yOffset || 0), z);
                model.rotation.y = rotY;
                if (opts.tiltX) model.rotation.x = opts.tiltX;
                if (opts.tiltZ) model.rotation.z = opts.tiltZ;
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                    }
                });
                this.scene.add(model);
            });
        };

        // --- Palm trees ---
        const palmModels = [
            'tree_palm.glb', 'tree_palmBend.glb', 'tree_palmDetailedShort.glb',
            'tree_palmDetailedTall.glb', 'tree_palmShort.glb', 'tree_palmTall.glb'
        ];

        for (let i = 0; i < 65; i++) {
            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.88;
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < 0.3) continue;

            const palm = palmModels[Math.floor(rand() * palmModels.length)];
            const scale = 8 + rand() * 5 + Math.max(0, h - 1) * 0.4;
            loadModel(palm, x, z, h, scale, rand() * Math.PI * 2, {
                tiltX: (rand() - 0.5) * 0.12,
                tiltZ: (rand() - 0.5) * 0.12
            });
        }

        // --- Rocks (variety of sizes) ---
        const largeRocks = ['rock_largeA.glb', 'rock_largeB.glb', 'rock_largeC.glb'];
        const smallRocks = ['rock_smallA.glb', 'rock_smallB.glb', 'rock_smallC.glb'];

        // Large rocks (big, dramatic boulders)
        for (let i = 0; i < 14; i++) {
            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.85;
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < 0.2) continue;

            const rock = largeRocks[Math.floor(rand() * largeRocks.length)];
            loadModel(rock, x, z, h, 6 + rand() * 6, rand() * Math.PI * 2, { yOffset: -0.5 });
        }

        // Small rocks (more numerous, still chunky)
        for (let i = 0; i < 30; i++) {
            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.90;
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < 0.15) continue;

            const rock = smallRocks[Math.floor(rand() * smallRocks.length)];
            loadModel(rock, x, z, h, 4 + rand() * 5, rand() * Math.PI * 2, { yOffset: -0.3 });
        }

        // Shore rocks (large boulders along the beach edge)
        for (let i = 0; i < 18; i++) {
            const angle = rand() * Math.PI * 2;
            const r = islandRadius * (0.82 + rand() * 0.12);
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < -0.5) continue;

            const rock = largeRocks[Math.floor(rand() * largeRocks.length)];
            loadModel(rock, x, z, h, 4 + rand() * 7, rand() * Math.PI * 2, { yOffset: -0.8 });
        }

        // --- Bushes ---
        const bushModels = ['plant_bush.glb', 'plant_bushLarge.glb', 'plant_bushSmall.glb'];
        for (let i = 0; i < 40; i++) {
            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.85;
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < 0.4) continue;

            const bush = bushModels[Math.floor(rand() * bushModels.length)];
            loadModel(bush, x, z, h, 5 + rand() * 4, rand() * Math.PI * 2);
        }

        // --- Flowers (colourful tropical accents) ---
        const flowerModels = ['flower_redA.glb', 'flower_yellowA.glb', 'flower_purpleA.glb'];
        for (let i = 0; i < 45; i++) {
            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.80;
            const x = Math.cos(angle) * r;
            const z = Math.sin(angle) * r;
            const h = this.getTerrainHeight(x, z);
            if (h < 0.5) continue;

            const flower = flowerModels[Math.floor(rand() * flowerModels.length)];
            loadModel(flower, x, z, h, 4 + rand() * 3, rand() * Math.PI * 2);
        }
    }

    async startGame() {
        const nameInput = document.getElementById('player-name');
        const name = nameInput.value.trim();
        if (!name) return;

        try {
            await this.auth.login(name, this.network);
        } catch (error) {
            alert('Could not connect to server. Please try again.');
        }
    }

    registerNetworkHandlers() {
        this.network.on('player_info', (data) => {
            this.log(`Player info received: UUID=${data.uuid}, Name=${data.name}`);

            // Update auth UUID for reconnect scenarios
            this.auth.playerUUID = data.uuid;

            if (this.playerModel) {
                // Reconnect: just recreate the local player at current position
                const pos = this.playerModel.position.clone();
                this.scene.remove(this.playerModel);
                this.playerModel = null;
                this.createLocalPlayer();
                this.playerModel.position.copy(pos);
                this.network.sendMove(this.playerModel.position, this.playerModel.rotation.y);
            } else {
                // First connect: show intro overlay
                document.getElementById('login-overlay').style.display = 'none';
                document.getElementById('intro-overlay').style.display = 'block';

                document.getElementById('fps-counter').style.display = 'none';
                document.getElementById('inventory-button').style.display = 'none';
                document.getElementById('show-fish-button').style.display = 'none';
                document.getElementById('chat-container').style.display = 'none';
                document.getElementById('game-log-wrapper').style.display = 'none';

                this.createLocalPlayer();
            }
        });

        this.network.on('player_joined', (data) => {
            if (data.uuid !== this.auth.playerUUID) {
                this.log(`Player joined: ${data.name} (${data.uuid})`);
                this.addGameLog(`${data.name} joined the island`, 'join');
                const playerModel = this.createPlayerModel(data.name);
                playerModel.position.set(data.position.x, data.position.y, data.position.z);
                // Interpolation buffer: stores recent server snapshots {time, pos, rot}
                const now = performance.now();
                playerModel.posBuffer = [
                    { t: now, x: data.position.x, y: data.position.y, z: data.position.z, r: data.rotation || 0 }
                ];
                this.scene.add(playerModel);
                this.players.set(data.uuid, playerModel);
                this.playerNames.set(data.uuid, data.name);
            }
        });

        this.network.on('player_left', (data) => {
            if (this.players.has(data.uuid)) {
                this.log(`Player left: ${this.playerNames.get(data.uuid)} (${data.uuid})`);
                this.addGameLog(`${this.playerNames.get(data.uuid)} left the island`, 'leave');
                const playerModel = this.players.get(data.uuid);
                this.scene.remove(playerModel);
                this.players.delete(data.uuid);
                this.playerNames.delete(data.uuid);

                if (this.fishingLines.has(data.uuid)) {
                    const fishingObj = this.fishingLines.get(data.uuid);
                    this.scene.remove(fishingObj.line);
                    this.scene.remove(fishingObj.bob);
                    this.fishingLines.delete(data.uuid);
                }
            }
        });

        this.network.on('game_tick', (data) => {
            for (const u of data.updates) {
                if (u.uuid === this.auth.playerUUID) {
                    // Skip updates for a different (stale) move command
                    if (u.ms !== undefined && u.ms !== this.currentMoveSeq) continue;

                    // Client already arrived locally — ignore walk updates, wait for server ARRIVED
                    if (!this.isMoving && u.s === 'walk') continue;

                    if (u.s === 'idle') {
                        // Server says arrived — smooth toward final position (don't hard snap)
                        if (this.playerModel) {
                            this._smoothTarget = { x: u.x, z: u.z, y: u.y, r: u.r };
                        }
                        console.log(`[reconcile] ARRIVED server=(${u.x.toFixed(2)}, ${u.z.toFixed(2)}) seq=${u.ms} ticks=${u.mt}`);
                        if (this.isMoving) this.stopMovement();
                        continue;
                    }

                    // --- SERVER RECONCILIATION (Gambetta) ---
                    const serverClientTicks = (u.mt || 0) * 2;
                    const ticksToReplay = Math.max(0, this.localMoveTicks - serverClientTicks);

                    let rx = u.x, rz = u.z;
                    const target = this.targetPosition;
                    if (target && ticksToReplay > 0) {
                        for (let i = 0; i < ticksToReplay; i++) {
                            const dx = target.x - rx;
                            const dz = target.z - rz;
                            const dist = Math.sqrt(dx * dx + dz * dz);
                            if (dist < this.minMoveDistance) break;
                            const nx = dx / dist;
                            const nz = dz / dist;
                            let speed = this.maxMoveSpeed;
                            if (dist < this.slowdownDistance) {
                                speed = Math.max(0.05, this.maxMoveSpeed * (dist / this.slowdownDistance));
                            }
                            rx += nx * speed;
                            rz += nz * speed;
                        }
                    }

                    // Set as smooth target instead of hard-snapping
                    if (this.playerModel) {
                        const prevX = this.playerModel.position.x;
                        const prevZ = this.playerModel.position.z;
                        const correction = Math.sqrt((rx - prevX) ** 2 + (rz - prevZ) ** 2);
                        // Small corrections (<1 unit): smooth lerp; large (>3): snap
                        if (correction > 3.0) {
                            this.playerModel.position.x = rx;
                            this.playerModel.position.z = rz;
                            this.playerModel.position.y = this.getTerrainHeight(rx, rz) + 2;
                            this._smoothTarget = null;
                        } else if (correction > 0.05) {
                            this._smoothTarget = { x: rx, z: rz, r: u.r };
                        }
                        this.playerModel.rotation.y = u.r;
                        console.log(`[reconcile] seq=${u.ms} serverTick=${u.mt} replayed=${ticksToReplay} correction=${correction.toFixed(3)}`);
                    }
                } else {
                    const rp = this.players.get(u.uuid);
                    if (!rp) continue;
                    // Push to interpolation buffer (pruned adaptively in render loop)
                    if (!rp.posBuffer) rp.posBuffer = [];
                    rp.posBuffer.push({ t: performance.now(), x: u.x, y: u.y, z: u.z, r: u.r });
                    if (u.s === 'walk') {
                        if (rp.walkAction && rp.idleAction) { rp.walkAction.play(); rp.idleAction.stop(); }
                    } else if (u.s === 'idle') {
                        if (rp.idleAction && rp.walkAction) { rp.idleAction.play(); rp.walkAction.stop(); }
                    }
                }
            }
        });

        this.network.on('player_moved', (data) => {
            if (data.uuid === this.auth.playerUUID) return;
            const targetPlayer = this.players.get(data.uuid);
            if (targetPlayer) {
                if (!targetPlayer.posBuffer) targetPlayer.posBuffer = [];
                targetPlayer.posBuffer.push({
                    t: performance.now(),
                    x: data.position.x, y: data.position.y, z: data.position.z,
                    r: data.rotation !== undefined ? data.rotation : (targetPlayer.rotation ? targetPlayer.rotation.y : 0)
                });
            }
        });

        this.network.on('position_correction', (data) => {
            this.log('Server corrected our position:', data.position);
            this.playerModel.position.set(data.position.x, data.position.y, data.position.z);
            this.isMoving = false;
            this.targetPosition = null;

            if (this.playerModel && this.playerModel.walkAction && this.playerModel.idleAction) {
                this.playerModel.walkAction.stop();
                this.playerModel.idleAction.play();
            }

            if (data.message) {
                this.showNotification(data.message);
            }
        });

        this.network.on('fishing_error', (data) => {
            this.showNotification(data.message);
            if (data.cooldown) {
                this.updateCooldownUI(data.cooldown);
            }
            this.removeFishingLine(this.auth.playerUUID);
        });

        this.network.on('animation_state', (data) => {
            // Legacy handler for non-movement animations
            const animPlayer = this.players.get(data.uuid);
            if (animPlayer) {
                if (data.state === 'walk' && animPlayer.walkAction) {
                    animPlayer.walkAction.play();
                    if (animPlayer.idleAction) animPlayer.idleAction.stop();
                } else if (data.state === 'idle' && animPlayer.idleAction) {
                    animPlayer.idleAction.play();
                    if (animPlayer.walkAction) animPlayer.walkAction.stop();
                }
            }
        });

        this.network.on('player_rotated', (data) => {
            if (data.uuid === this.auth.playerUUID) return;
            const rp = this.players.get(data.uuid);
            if (rp && rp.posBuffer && rp.posBuffer.length > 0) {
                // Update rotation on the latest buffer entry
                const last = rp.posBuffer[rp.posBuffer.length - 1];
                rp.posBuffer.push({ t: performance.now(), x: last.x, y: last.y, z: last.z, r: data.rotation });
            }
        });

        this.network.on('line_cast', (data) => {
            this.createFishingLine(data.uuid, data.position);
        });

        this.network.on('line_retracted', (data) => {
            this.removeFishingLine(data.uuid);
        });

        this.network.on('fish_caught', (data) => {
            this.showCaughtFish(data.fish);
        });

        this.network.on('fish_hooked', (data) => {
            this.startMinigame(data.fish_difficulty);
        });

        this.network.on('fishing_cancelled', () => {
            this.endMinigame(false, true);
        });

        this.network.on('notification', (data) => {
            this.showNotification(data.message);
        });

        this.network.on('show_fish_cooldown', (data) => {
            this.updateCooldownUI(data.cooldown);
        });

        this.network.on('player_showing_fish', (data) => {
            const player = data.uuid === this.auth.playerUUID ? this.playerModel : this.players.get(data.uuid);
            if (player) {
                if (player.fishDisplay) {
                    player.remove(player.fishDisplay);
                    this.scene.remove(player.fishDisplay);
                    player.fishDisplay = null;
                }
                this.createProceduralFish(data.fish, player);
            }
        });

        this.network.on('player_showing_fish_ended', (data) => {
            const playerWithFish = this.players.get(data.uuid);
            if (playerWithFish && playerWithFish.fishDisplay) {
                playerWithFish.remove(playerWithFish.fishDisplay);
                this.scene.remove(playerWithFish.fishDisplay);
                playerWithFish.fishDisplay = null;
            }
        });

        this.network.on('chat_message', (data) => {
            this.addChatMessage(data.name, data.message);
        });

        this.network.on('_reconnecting', () => {
            this.showNotification('Connection lost. Reconnecting...', 'error');
        });

        this.network.on('_reconnected', () => {
            // Clear stale remote player models
            for (const [uuid, model] of this.players) {
                this.scene.remove(model);
                if (this.fishingLines.has(uuid)) {
                    const fishingObj = this.fishingLines.get(uuid);
                    this.scene.remove(fishingObj.line);
                    this.scene.remove(fishingObj.bob);
                    this.fishingLines.delete(uuid);
                }
            }
            this.players.clear();
            this.playerNames.clear();

            // Remove old local player model so createLocalPlayer can make a fresh one
            if (this.playerModel) {
                this.scene.remove(this.playerModel);
                this.playerModel = null;
            }

            this.showNotification('Reconnected!', 'success');
        });

        this.network.on('_disconnected', () => {
            alert('Connection to server lost. Please refresh the page to reconnect.');
        });
    }

    createLocalPlayer() {
        this.playerModel = this.createPlayerModel(this.auth.playerName);
        
        // Position player at the center of the island
        this.playerModel.position.set(0, this.getTerrainHeight(0, 0) + 2, 0);
        this._cameraTarget = { x: 0, z: 0 };
        this.scene.add(this.playerModel);
        
        // Send initial position to server
        this.network.sendMove(this.playerModel.position, this.playerModel.rotation.y);
    }

    createPlayerModel(name) {
        const group = new THREE.Group();

        // Load the puppet kid model
        const loader = new GLTFLoader();
        loader.load('/assets/nature_props/gltf/puppet_kid.glb', 
            (gltf) => {
                const model = gltf.scene;
                if (!model) {
                    console.error('Failed to load model');
                    return;
                }

                // Scale down the model
                model.scale.setScalar(3);
                
                // Fix initial rotation to face forward
                model.rotation.y = 0; // No rotation to face forward
                
                // Enable shadows for the model
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                        // Ensure proper shadow rendering
                        child.frustumCulled = false;
                    }
                });

                // Print out all animations
                this.log('Available animations:');
                if (gltf.animations && gltf.animations.length > 0) {
                    gltf.animations.forEach((clip, index) => {
                        this.log(`${index}: ${clip.name}`);
                    });
                }

                // Add fishing rod
                const rodGroup = new THREE.Group();
                const rodGeometry = new THREE.CylinderGeometry(0.05, 0.05, 3);
                const rodMaterial = new THREE.MeshPhongMaterial({ 
                    color: 0x8b4513,
                    shininess: 30
                });
                const rod = new THREE.Mesh(rodGeometry, rodMaterial);
                rod.name = 'Rod';
                rod.castShadow = true;
                // handmade values
                rod.rotation.x = -1.27;
                rod.rotation.y = 0.09;
                rod.rotation.z = 2.13;
                rod.position.set(1.50, -0.19, -0.31);
                rodGroup.add(rod);

                // Find and attach rod to the right hand bone
                model.traverse((child) => {
                    if (child.isBone && child.name === 'RightHand_016') {
                        const pivot = new THREE.Group();
                        pivot.rotation.x = -Math.PI / 6;
                        pivot.add(rodGroup);
                        child.add(pivot);
                    }
                });

                group.add(model);
                group.model = model;
                group.rodGroup = rodGroup;

                // Store the mixer and actions
                if (gltf.animations && gltf.animations.length > 0) {
                    group.mixer = new THREE.AnimationMixer(model);
                    
                    // Find both idle and walk animations
                    const idleClip = gltf.animations.find(clip => 
                        clip.name.toLowerCase().includes('idle')
                    );
                    const walkClip = gltf.animations.find(clip => 
                        clip.name.toLowerCase().includes('walk') || 
                        clip.name.toLowerCase().includes('run')
                    );
                    
                    if (idleClip) {
                        const idleAction = group.mixer.clipAction(idleClip);
                        idleAction.play();
                        group.idleAction = idleAction;
                        this.log('Playing idle animation:', idleClip.name);
                    }
                    
                    if (walkClip) {
                        const walkAction = group.mixer.clipAction(walkClip);
                        walkAction.play();
                        walkAction.stop(); // Start stopped
                        group.walkAction = walkAction;
                        this.log('Found walk animation:', walkClip.name);
                    }

                    // Print all available animations for debugging
                    this.log('All available animations:');
                    gltf.animations.forEach((clip, index) => {
                        this.log(`${index}: ${clip.name}`);
                    });
                }
            },
            (progress) => {
                this.log(`Loading model: ${(progress.loaded / progress.total * 100)}%`);
            },
            (error) => {
                console.error('Error loading model:', error);
            }
        );

        // Name tag with enhanced visibility
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 64;
        const context = canvas.getContext('2d');
        context.fillStyle = 'rgba(0, 0, 0, 0.5)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.font = 'Bold 40px Arial';
        context.fillStyle = 'white';
        context.textAlign = 'center';
        context.fillText(name, 128, 44);
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.y = 7;
        sprite.scale.set(10, 2.5, 1);
        group.add(sprite);

        return group;
    }

    // Message handling is now in registerNetworkHandlers()

    createFishingLine(player, position) {
        if (this.fishingLines.has(player)) {
            this.removeFishingLine(player);
        }

        const playerModel = player === this.auth.playerUUID ? this.playerModel : this.players.get(player);
        if (!playerModel) return;

        // Rotate player model to face the fishing spot
        const direction = new THREE.Vector3()
            .subVectors(new THREE.Vector3(position.x, 0, position.z), 
                        new THREE.Vector3(playerModel.position.x, 0, playerModel.position.z))
            .normalize();

        // Calculate the angle for rotation
        const angle = Math.atan2(direction.x, direction.z);
        playerModel.rotation.y = angle;

        // Create the line with updated material
        const material = new THREE.LineBasicMaterial({ 
            color: 0x000000,
            linewidth: 2  // Note: linewidth may not work in WebGL
        });
        
        let rodTopPosition = new THREE.Vector3();
        let foundRod = false;

        playerModel.traverse((child) => {
            if (child.isMesh && child.name === 'Rod') {
                const rodTipLocal = new THREE.Vector3(0, -1.5, 0);
                rodTopPosition.copy(rodTipLocal).applyMatrix4(child.matrixWorld);
                foundRod = true;
            }
        });

        // Fallback if rod mesh not loaded yet (common on mobile)
        if (!foundRod) {
            rodTopPosition.set(
                playerModel.position.x + direction.x * 2,
                playerModel.position.y + 3,
                playerModel.position.z + direction.z * 2
            );
        }

        // Create fishing bob (red ball)
        const bobGeometry = new THREE.SphereGeometry(0.15, 8, 8);
        const bobMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 }); // Red
        const bob = new THREE.Mesh(bobGeometry, bobMaterial);
        bob.position.set(position.x, 0.1, position.z); // Just above water
        this.scene.add(bob);

        // Create line from rod to bob
        const points = [
            rodTopPosition,
            new THREE.Vector3(position.x, 0.1, position.z)
        ];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(geometry, material);
        this.scene.add(line);
        
        // Store both the line and bob
        this.fishingLines.set(player, { line, bob });
    }

    removeFishingLine(player) {
        if (this.fishingLines.has(player)) {
            const fishingObj = this.fishingLines.get(player);
            
            // Remove line and bob from scene
            this.scene.remove(fishingObj.line);
            this.scene.remove(fishingObj.bob);
            
            this.fishingLines.delete(player);
        }
    }

    // Update fishing lines when camera or characters move
    updateFishingLines() {
        this.fishingLines.forEach((fishingObj, player) => {
            const playerModel = player === this.auth.playerUUID ? this.playerModel : this.players.get(player);
            if (!playerModel) return;
            
            let rodTopPosition = new THREE.Vector3();
            let foundRod = false;

            playerModel.traverse((child) => {
                if (child.isMesh && child.name === 'Rod') {
                    const rodTipLocal = new THREE.Vector3(0, -1.5, 0);
                    rodTopPosition.copy(rodTipLocal).applyMatrix4(child.matrixWorld);
                    foundRod = true;
                }
            });

            if (!foundRod) {
                const dir = new THREE.Vector3(0, 0, 1).applyEuler(playerModel.rotation);
                rodTopPosition.set(
                    playerModel.position.x + dir.x * 2,
                    playerModel.position.y + 3,
                    playerModel.position.z + dir.z * 2
                );
            }
            
            // Update the line geometry
            const bobPosition = fishingObj.bob.position;
            const points = [rodTopPosition, bobPosition];
            fishingObj.line.geometry.dispose();
            fishingObj.line.geometry = new THREE.BufferGeometry().setFromPoints(points);
            
            // Add a small bobbing motion to the bob
            const bobOffset = Math.sin(Date.now() * 0.003) * 0.05;
            fishingObj.bob.position.y = 0.1 + bobOffset;
        });
    }

    createInventoryUI() {
        // Reference UI elements
        this.inventoryButton = document.getElementById('inventory-button');
        this.inventoryMenu = document.getElementById('inventory-menu');
        this.showFishButton = document.getElementById('show-fish-button');
        this.cooldownIndicator = document.getElementById('cooldown-indicator');
        this.highlightedFish = null;

        // Add click handlers
        this.inventoryButton.onclick = () => this.toggleInventoryMenu();
        document.getElementById('inventory-close').onclick = () => this.toggleInventoryMenu();
        this.showFishButton.onclick = () => this.showLastCaughtFish();
    }

    toggleInventoryMenu() {
        const isVisible = this.inventoryMenu.style.display === 'block';
        this.inventoryMenu.style.display = isVisible ? 'none' : 'block';
        if (!isVisible) {
            this.updateInventoryContent();
        }
    }

    updateInventoryContent() {
        const content = document.getElementById('inventory-content');
        if (!content) return;

        content.innerHTML = '';

        if (this.inventory.length === 0) {
            content.innerHTML = '<p style="text-align: center;">No fish caught yet</p>';
            return;
        }

        // Create fish list
        this.inventory.forEach((fish, index) => {
            const fishItem = document.createElement('div');
            fishItem.className = 'fish-item';

            // Get rarity information
            const rarityNames = {1: "Common", 2: "Uncommon", 3: "Rare", 4: "Epic", 5: "Legendary"};
            const rarityColors = {
                1: "#AAAAAA",
                2: "#55AA55",
                3: "#5555FF",
                4: "#AA55AA",
                5: "#FFAA00"
            };
            const rarityName = rarityNames[fish.rarity] || "";
            const color = rarityColors[fish.rarity] || "#FFFFFF";

            // Fish info
            const fishInfo = document.createElement('div');
            fishInfo.className = 'fish-info';
            fishInfo.style.color = color;
            fishInfo.innerHTML = `
                <div><strong>${rarityName} ${fish.type}</strong></div>
                <div style="font-size: 0.9em;">Size: ${fish.size.toFixed(1)}cm, Weight: ${fish.weight.toFixed(1)}kg</div>
            `;

            // Buttons container
            const buttons = document.createElement('div');
            buttons.className = 'fish-buttons';

            // Highlight button
            const highlightBtn = document.createElement('button');
            highlightBtn.className = `fish-button highlight-button ${this.highlightedFish === fish ? 'selected' : ''}`;
            highlightBtn.textContent = this.highlightedFish === fish ? '⭐ Selected' : 'Select';
            highlightBtn.onclick = () => {
                this.highlightedFish = this.highlightedFish === fish ? null : fish;
                this.updateInventoryContent(); // Refresh the display
            };

            // Discard button
            const discardBtn = document.createElement('button');
            discardBtn.className = 'fish-button discard-button';
            discardBtn.textContent = '🗑️';
            discardBtn.onclick = () => {
                if (confirm(`Are you sure you want to discard this ${rarityName} ${fish.type}?`)) {
                    this.inventory.splice(index, 1);
                    if (this.highlightedFish === fish) {
                        this.highlightedFish = null;
                    }
                    this.updateInventoryContent();
                }
            };

            buttons.appendChild(highlightBtn);
            buttons.appendChild(discardBtn);

            fishItem.appendChild(fishInfo);
            fishItem.appendChild(buttons);
            content.appendChild(fishItem);
        });
    }

    showLastCaughtFish() {
        if (this.inventory.length === 0) {
            this.showNotification("You don't have any fish to show!");
            return;
        }
        
        // Use highlighted fish if available, otherwise use the most recent
        const fishToShow = this.highlightedFish || this.inventory[this.inventory.length - 1];
        
        // Send request to server with selected fish data
        this.network.sendShowFish({
            type: fishToShow.type,
            size: fishToShow.size,
            weight: fishToShow.weight
        });
    }

    // Create a procedurally generated fish based on size and weight
    createProceduralFish(fish, playerModel) {
        // Create a group to hold the fish
        const fishGroup = new THREE.Group();
        
        // Get rarity information
        const rarity = fish.rarity || 1;
        
        // Scale factors based on fish size and weight
        // For legendary fish (rarity 5), we need to scale down the visual representation
        // while preserving the actual size values
        const visualScaleFactor = rarity === 5 ? 0.1 : (rarity === 4 ? 0.3 : 1.0);
        const sizeScale = (fish.size / 100) * visualScaleFactor; // Normalize size
        const weightFactor = (fish.weight / 50) * visualScaleFactor; // Normalize weight
        
        // Body shape (ellipsoid)
        const bodyWidth = 1.5 * sizeScale;
        const bodyHeight = 0.8 * sizeScale;
        const bodyLength = 3 * sizeScale * (1 + weightFactor * 0.5); // Longer for heavier fish
        
        // Create body
        const bodyGeometry = new THREE.SphereGeometry(1, 16, 12);
        bodyGeometry.scale(bodyLength, bodyHeight, bodyWidth);
        
        // Parse the color (remove 0x prefix if present)
        let colorValue = fish.color;
        if (typeof colorValue === 'string' && colorValue.startsWith('0x')) {
            colorValue = parseInt(colorValue.substring(2), 16);
        }
        
        // Add glow effect for rare fish
        const bodyMaterial = new THREE.MeshPhongMaterial({
            color: colorValue,
            shininess: 80 + (rarity * 20), // Higher shininess for rarer fish
            specular: rarity >= 4 ? 0xffffff : 0x666666 // Brighter specular for epic/legendary
        });
        
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        fishGroup.add(body);
        
        // Add eyes (two small white spheres with black pupils)
        const eyeGeometry = new THREE.SphereGeometry(0.15 * sizeScale, 8, 8);
        const eyeMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
        const pupilGeometry = new THREE.SphereGeometry(0.07 * sizeScale, 8, 8);
        const pupilMaterial = new THREE.MeshPhongMaterial({ color: 0x000000 });
        
        // Left eye
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(bodyLength * 0.6, bodyHeight * 0.5, bodyWidth * 0.7);
        fishGroup.add(leftEye);
        
        const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
        leftPupil.position.set(bodyLength * 0.65, bodyHeight * 0.5, bodyWidth * 0.85);
        fishGroup.add(leftPupil);
        
        // Right eye
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(bodyLength * 0.6, bodyHeight * 0.5, -bodyWidth * 0.7);
        fishGroup.add(rightEye);
        
        const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
        rightPupil.position.set(bodyLength * 0.65, bodyHeight * 0.5, -bodyWidth * 0.85);
        fishGroup.add(rightPupil);
        
        // Add mouth (curved line)
        const mouthShape = new THREE.Shape();
        mouthShape.moveTo(-0.2 * sizeScale, 0);
        mouthShape.quadraticCurveTo(0, -0.3 * sizeScale, 0.2 * sizeScale, 0);
        
        const mouthGeometry = new THREE.ShapeGeometry(mouthShape);
        const mouthMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
        const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial);
        mouth.position.set(bodyLength * 0.8, -bodyHeight * 0.2, 0);
        mouth.rotation.y = Math.PI / 2;
        fishGroup.add(mouth);
        
        // Add fins
        const finMaterial = new THREE.MeshPhongMaterial({
            color: colorValue,
            transparent: true,
            opacity: 0.8,
            side: THREE.DoubleSide
        });
        
        // Top fin
        const topFinShape = new THREE.Shape();
        topFinShape.moveTo(0, 0);
        topFinShape.lineTo(-bodyLength * 0.5, bodyHeight * 1.5);
        topFinShape.lineTo(bodyLength * 0.5, bodyHeight * 1.5);
        topFinShape.lineTo(0, 0);
        
        const topFinGeometry = new THREE.ShapeGeometry(topFinShape);
        const topFin = new THREE.Mesh(topFinGeometry, finMaterial);
        topFin.position.set(0, bodyHeight, 0);
        topFin.rotation.y = Math.PI / 2;
        fishGroup.add(topFin);
        
        // Side fins
        const sideFinShape = new THREE.Shape();
        sideFinShape.moveTo(0, 0);
        sideFinShape.lineTo(-bodyWidth * 0.8, -bodyHeight * 0.8);
        sideFinShape.lineTo(bodyWidth * 0.8, -bodyHeight * 0.8);
        sideFinShape.lineTo(0, 0);
        
        const sideFinGeometry = new THREE.ShapeGeometry(sideFinShape);
        
        // Left side fin
        const leftSideFin = new THREE.Mesh(sideFinGeometry, finMaterial);
        leftSideFin.position.set(0, 0, bodyWidth);
        leftSideFin.rotation.x = Math.PI / 4;
        fishGroup.add(leftSideFin);
        
        // Right side fin
        const rightSideFin = new THREE.Mesh(sideFinGeometry, finMaterial);
        rightSideFin.position.set(0, 0, -bodyWidth);
        rightSideFin.rotation.x = -Math.PI / 4;
        fishGroup.add(rightSideFin);
        
        // Tail fin
        const tailFinShape = new THREE.Shape();
        tailFinShape.moveTo(0, 0);
        tailFinShape.lineTo(-bodyWidth * 1.5, bodyHeight * 1.2);
        tailFinShape.lineTo(-bodyWidth * 1.5, -bodyHeight * 1.2);
        tailFinShape.lineTo(0, 0);
        
        const tailFinGeometry = new THREE.ShapeGeometry(tailFinShape);
        const tailFin = new THREE.Mesh(tailFinGeometry, finMaterial);
        tailFin.position.set(-bodyLength, 0, 0);
        tailFin.rotation.y = Math.PI / 2;
        fishGroup.add(tailFin);
        
        // Add rarity indicator (glowing particles for rare fish)
        if (rarity >= 3) {
            const particleCount = rarity * 10;
            const particles = new THREE.Group();
            
            for (let i = 0; i < particleCount; i++) {
                const particleGeometry = new THREE.SphereGeometry(0.05 * sizeScale, 8, 8);
                const particleColor = rarity === 5 ? 0xffff00 : (rarity === 4 ? 0xff00ff : 0x00ffff);
                const particleMaterial = new THREE.MeshBasicMaterial({ 
                    color: particleColor,
                    transparent: true,
                    opacity: 0.7
                });
                
                const particle = new THREE.Mesh(particleGeometry, particleMaterial);
                
                // Random position around the fish
                const angle = Math.random() * Math.PI * 2;
                const radius = (bodyWidth + bodyLength) * 0.7;
                particle.position.set(
                    (Math.random() - 0.5) * bodyLength * 2,
                    (Math.random() - 0.5) * bodyHeight * 2,
                    (Math.random() - 0.5) * bodyWidth * 2
                );
                
                particles.add(particle);
            }
            
            fishGroup.add(particles);
            
            // Animate particles
            const animateParticles = () => {
                particles.children.forEach(particle => {
                    particle.position.x += (Math.random() - 0.5) * 0.01;
                    particle.position.y += (Math.random() - 0.5) * 0.01;
                    particle.position.z += (Math.random() - 0.5) * 0.01;
                });
            };
            
            // Store animation function
            fishGroup.animateParticles = animateParticles;
        }
        
        // Position the fish above the player
        // For legendary fish, position them higher to avoid clipping
        const heightOffset = rarity >= 5 ? 15 : (rarity >= 4 ? 12 : 8);
        fishGroup.position.set(0, heightOffset, 0);
        
        // Add fish info label
        const rarityNames = {1: "Common", 2: "Uncommon", 3: "Rare", 4: "Epic", 5: "Legendary"};
        const rarityName = rarityNames[rarity] || "";
        
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        context.fillStyle = 'rgba(0, 0, 0, 0.7)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Set text color based on rarity
        const rarityColors = {
            1: "#AAAAAA", // Common: gray
            2: "#55AA55", // Uncommon: green
            3: "#5555FF", // Rare: blue
            4: "#AA55AA", // Epic: purple
            5: "#FFAA00"  // Legendary: orange
        };
        
        context.font = 'Bold 40px Arial';
        context.fillStyle = rarityColors[rarity] || 'white';
        context.textAlign = 'center';
        context.fillText(`${rarityName} ${fish.type}`, 256, 50);
        context.font = '30px Arial';
        context.fillText(`${fish.size.toFixed(1)}cm, ${fish.weight.toFixed(1)}kg`, 256, 90);
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.y = heightOffset + 5;
        sprite.scale.set(15, 4, 1);
        fishGroup.add(sprite);
        
        // Add to scene and attach to player
        this.scene.add(fishGroup);
        playerModel.add(fishGroup);
        
        // Store reference to remove later
        playerModel.fishDisplay = fishGroup;
        
        // Set up animation
        const startTime = Date.now();
        const displayDuration = 10000; // 10 seconds (changed from 30000)
        const animateFish = () => {
            const elapsed = Date.now() - startTime;
            
            // Gentle bobbing motion
            fishGroup.position.y = heightOffset + Math.sin(elapsed * 0.003) * 0.2;
            
            // Gentle rotation
            fishGroup.rotation.y = Math.sin(elapsed * 0.002) * 0.2;
            
            // Tail wiggle
            if (tailFin) {
                tailFin.rotation.z = Math.sin(elapsed * 0.01) * 0.2;
            }
            
            // Animate particles if they exist
            if (fishGroup.animateParticles) {
                fishGroup.animateParticles();
            }
            
            // Remove after display duration
            if (elapsed < displayDuration) {
                requestAnimationFrame(animateFish);
            } else {
                playerModel.remove(fishGroup);
                this.scene.remove(fishGroup);
                playerModel.fishDisplay = null;
            }
        };
        
        animateFish();
        
        return fishGroup;
    }

    updateCooldownUI(cooldownSeconds) {
        if (!this.cooldownIndicator || !this.showFishButton) return;
        
        // Disable button during cooldown
        this.showFishButton.disabled = cooldownSeconds > 0;
        this.showFishButton.style.opacity = cooldownSeconds > 0 ? '0.5' : '1';
        
        if (cooldownSeconds > 0) {
            // Show cooldown indicator
            this.cooldownIndicator.style.display = 'block';
            this.cooldownIndicator.textContent = `Cooldown: ${Math.ceil(cooldownSeconds)}s`;
            
            // Start countdown
            if (this.cooldownInterval) {
                clearInterval(this.cooldownInterval);
            }
            
            const startTime = Date.now();
            const endTime = startTime + (cooldownSeconds * 1000);
            
            this.cooldownInterval = setInterval(() => {
                const remaining = Math.max(0, (endTime - Date.now()) / 1000);
                
                if (remaining <= 0) {
                    // Cooldown finished
                    clearInterval(this.cooldownInterval);
                    this.cooldownIndicator.style.display = 'none';
                    this.showFishButton.disabled = false;
                    this.showFishButton.style.opacity = '1';
                } else {
                    // Update countdown
                    this.cooldownIndicator.textContent = `Cooldown: ${Math.ceil(remaining)}s`;
                }
            }, 1000);
        } else {
            // Hide cooldown indicator
            this.cooldownIndicator.style.display = 'none';
            if (this.cooldownInterval) {
                clearInterval(this.cooldownInterval);
            }
        }
    }

    showCaughtFish(fish) {
        // Add fish to inventory
        if (!this.inventory) {
            this.inventory = [];
        }
        this.inventory.push(fish);
        
        // Update inventory UI
        this.updateInventoryContent();
        
        // Get rarity name for display
        const rarityNames = {1: "Common", 2: "Uncommon", 3: "Rare", 4: "Epic", 5: "Legendary"};
        const rarityName = rarityNames[fish.rarity] || "";
        
        // Show notification toast
        this.showNotification(`You caught a ${rarityName} ${fish.type}! (${fish.size.toFixed(1)}cm, ${fish.weight.toFixed(1)}kg)`, 'fish');
    }

    startMinigame(difficulty) {
        if (this.minigameActive) return;
        this.minigameActive = true;
        this.minigameFishDifficulty = difficulty;
        this.minigameFishPos = 0.5;
        this.minigameReelPos = 0.5;
        this.minigameOverlapTime = 0;
        this.minigameTimeLeft = 15;
        this.minigameFishDir = 1;
        this.minigameFishTimer = 0;
        this.minigameReelVelocity = 0;

        // Save camera state
        this._savedCameraState = {
            angle: this.cameraAngle,
            zoom: this.cameraZoom,
            target: { x: this._cameraTarget.x, z: this._cameraTarget.z }
        };

        // Show UI
        document.getElementById('fishing-minigame').style.display = 'block';
        document.getElementById('reel-status').textContent = 'A fish is biting!';
        document.getElementById('reel-progress-fill').style.height = '0%';

        // Button input
        const btn = document.getElementById('reel-button');
        this._reelButtonDown = (e) => {
            e.preventDefault();
            e.stopPropagation();
            btn.classList.add('pressed');
            this.minigameReelVelocity += 0.12;
        };
        this._reelButtonUp = (e) => {
            btn.classList.remove('pressed');
        };
        btn.addEventListener('mousedown', this._reelButtonDown);
        btn.addEventListener('touchstart', this._reelButtonDown);
        btn.addEventListener('mouseup', this._reelButtonUp);
        btn.addEventListener('mouseleave', this._reelButtonUp);
        btn.addEventListener('touchend', this._reelButtonUp);
    }

    updateMinigame(dt) {
        if (!this.minigameActive) return;

        const difficulty = this.minigameFishDifficulty;
        const fishSpeed = 0.3 + difficulty * 0.12;

        // Fish movement — random direction changes
        this.minigameFishTimer -= dt;
        if (this.minigameFishTimer <= 0) {
            this.minigameFishDir = Math.random() > 0.5 ? 1 : -1;
            this.minigameFishTimer = 0.4 + Math.random() * (1.5 - difficulty * 0.15);
        }
        this.minigameFishPos += this.minigameFishDir * fishSpeed * dt;
        if (this.minigameFishPos <= 0) { this.minigameFishPos = 0; this.minigameFishDir = 1; }
        if (this.minigameFishPos >= 1) { this.minigameFishPos = 1; this.minigameFishDir = -1; }

        // Reel indicator — gravity pulls down, button pushes up
        this.minigameReelVelocity -= 0.4 * dt; // gravity
        this.minigameReelPos += this.minigameReelVelocity;
        this.minigameReelVelocity *= 0.92; // damping
        if (this.minigameReelPos <= 0) { this.minigameReelPos = 0; this.minigameReelVelocity = 0; }
        if (this.minigameReelPos >= 1) { this.minigameReelPos = 1; this.minigameReelVelocity = 0; }

        // Overlap check — indicator covers ~18% of the bar
        const overlap = Math.abs(this.minigameFishPos - this.minigameReelPos) < 0.12;
        if (overlap) {
            this.minigameOverlapTime += dt;
        }

        // Timer
        this.minigameTimeLeft -= dt;

        // Win / lose
        if (this.minigameOverlapTime >= 4.0) {
            this.endMinigame(true);
            return;
        }
        if (this.minigameTimeLeft <= 0) {
            this.endMinigame(false);
            return;
        }

        // Update DOM
        const trackH = 380;
        const fishEl = document.getElementById('reel-fish');
        const indEl = document.getElementById('reel-indicator');
        const progressEl = document.getElementById('reel-progress-fill');
        const statusEl = document.getElementById('reel-status');

        // Fish and indicator positioned from top (0 = top, 1 = bottom inverted)
        fishEl.style.top = ((1 - this.minigameFishPos) * (trackH - 36)) + 'px';
        indEl.style.top = ((1 - this.minigameReelPos) * (trackH - 70)) + 'px';

        // Color indicator based on overlap
        indEl.style.background = overlap
            ? 'rgba(76, 175, 80, 0.7)'
            : 'rgba(76, 175, 80, 0.35)';
        indEl.style.borderColor = overlap
            ? 'rgba(76, 175, 80, 1)'
            : 'rgba(76, 175, 80, 0.6)';

        // Progress bar (0-100%)
        progressEl.style.height = Math.min(100, (this.minigameOverlapTime / 4.0) * 100) + '%';

        // Status text
        const secs = Math.ceil(this.minigameTimeLeft);
        statusEl.textContent = overlap ? `Reeling! ${secs}s` : `Keep it steady! ${secs}s`;
    }

    endMinigame(success, silent = false) {
        if (!this.minigameActive) return;
        this.minigameActive = false;

        // Hide UI
        document.getElementById('fishing-minigame').style.display = 'none';

        // Clean up button listeners
        const btn = document.getElementById('reel-button');
        btn.classList.remove('pressed');
        if (this._reelButtonDown) {
            btn.removeEventListener('mousedown', this._reelButtonDown);
            btn.removeEventListener('touchstart', this._reelButtonDown);
        }
        if (this._reelButtonUp) {
            btn.removeEventListener('mouseup', this._reelButtonUp);
            btn.removeEventListener('mouseleave', this._reelButtonUp);
            btn.removeEventListener('touchend', this._reelButtonUp);
        }

        // Restore camera
        if (this._savedCameraState) {
            this.cameraAngle = this._savedCameraState.angle;
            this.cameraZoom = this._savedCameraState.zoom;
            this._cameraTarget.x = this._savedCameraState.target.x;
            this._cameraTarget.z = this._savedCameraState.target.z;
            this._savedCameraState = null;
        }

        // Send result to server
        if (!silent) {
            this.network.sendMinigameResult(success);
        }

        if (!success && !silent) {
            this.showNotification('The fish got away!');
        }

        this.isFishing = false;
        this.isCasting = false;
    }

    setupGameControls() {
        // Show game UI elements
        document.getElementById('fps-counter').style.display = this.debug ? 'block' : 'none';
        document.getElementById('inventory-button').style.display = 'block';
        document.getElementById('show-fish-button').style.display = 'block';
        document.getElementById('chat-container').style.display = 'flex';
        document.getElementById('chat-button').style.display = 'block';

        const el = this.renderer.domElement;
        const clampZoom = (z) => Math.max(1.0, Math.min(3.0, z));

        // --- Touch controls ---
        let dragStartX = 0, dragStartAngle = 0, tapMoved = false;
        let lastTouchX = 0;
        let lastTapTime = 0, lastTapX = 0, lastTapY = 0;
        let isPinching = false, pinchStartDist = 0, pinchStartZoom = 0;

        const getTouchDist = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

        el.addEventListener('touchstart', (e) => {
            if (e.touches.length === 2) {
                isPinching = true;
                tapMoved = true;
                pinchStartDist = getTouchDist(e.touches[0], e.touches[1]);
                pinchStartZoom = this.cameraZoom;
            } else if (e.touches.length === 1 && !isPinching) {
                dragStartX = e.touches[0].clientX;
                dragStartAngle = this.cameraAngle;
                lastTouchX = e.touches[0].clientX;
                tapMoved = false;
                this.cameraVelocity = 0;
            }
        }, { passive: true });

        el.addEventListener('touchmove', (e) => {
            if (e.touches.length === 2 && isPinching) {
                e.preventDefault();
                const dist = getTouchDist(e.touches[0], e.touches[1]);
                const scale = dist / pinchStartDist;
                this.cameraZoom = clampZoom(pinchStartZoom - (scale - 1) * 1.5);
            } else if (e.touches.length === 1 && !isPinching) {
                const currentX = e.touches[0].clientX;
                const dx = currentX - dragStartX;
                if (Math.abs(dx) > 10) {
                    tapMoved = true;
                }
                // Only update camera angle/velocity once drag threshold is crossed
                if (tapMoved) {
                    this.cameraVelocity = (currentX - lastTouchX) * 0.005;
                    lastTouchX = currentX;
                    this.cameraAngle = dragStartAngle - dx * 0.006;
                }
            }
        }, { passive: false });

        el.addEventListener('touchend', (e) => {
            if (e.touches.length < 2) isPinching = false;
            // Restore camera angle if this was a tap (not a drag)
            if (!tapMoved && e.touches.length === 0) {
                this.cameraAngle = dragStartAngle;
                this.cameraVelocity = 0;
            }
            if (e.touches.length === 0 && !tapMoved && e.changedTouches.length === 1) {
                const touch = e.changedTouches[0];
                const now = Date.now();
                const dist = Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY);
                if (now - lastTapTime < 300 && dist < 50) {
                    this.handleTapOrClick(touch.clientX, touch.clientY, true);
                    lastTapTime = 0;
                } else {
                    lastTapTime = now;
                    lastTapX = touch.clientX;
                    lastTapY = touch.clientY;
                }
            }
        }, { passive: true });

        // --- Mouse controls ---
        let mouseDown = false, mouseStartX = 0, mouseStartAngle = 0, mouseMoved = false;

        el.addEventListener('mousedown', (e) => {
            mouseDown = true;
            mouseStartX = e.clientX;
            mouseStartAngle = this.cameraAngle;
            mouseMoved = false;
            this.cameraVelocity = 0;
        });

        window.addEventListener('mousemove', (e) => {
            if (!mouseDown) return;
            const dx = e.clientX - mouseStartX;
            if (Math.abs(dx) > 3) mouseMoved = true;
            this.cameraAngle = mouseStartAngle - dx * 0.006;
        });

        window.addEventListener('mouseup', () => { mouseDown = false; });

        el.addEventListener('dblclick', (e) => {
            if (!mouseMoved) {
                this.handleTapOrClick(e.clientX, e.clientY, true);
            }
        });

        el.addEventListener('contextmenu', (e) => e.preventDefault());

        // Wheel zoom
        el.addEventListener('wheel', (e) => {
            e.preventDefault();
            this.cameraZoom = clampZoom(this.cameraZoom + e.deltaY * 0.001);
        }, { passive: false });

        // Keyboard
        document.addEventListener('keydown', (e) => {
            if (this.chatInputActive) return;
            if (e.key === 't' || e.key === 'T') {
                this.toggleChatInput(true);
                e.preventDefault();
            }
        });
    }
    
    handleTapOrClick(clientX, clientY, isDoubleTap) {
        // Skip interaction during minigame or chat
        if (this.minigameActive) return;
        if (this.chatInputActive) return;
        
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        mouse.x = (clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(clientY / window.innerHeight) * 2 + 1;
        raycaster.setFromCamera(mouse, this.camera);
        
        const intersects = raycaster.intersectObjects([this.ocean, this.island]);

        if (intersects.length > 0) {
            const clickPoint = intersects[0].point;
            const terrainH = this.getTerrainHeight(clickPoint.x, clickPoint.z);
            const isOnIsland = terrainH > 0.3;

            if (isDoubleTap && !isOnIsland) {
                // Fishing action — click on water
                const isClickOnWater = intersects[0].object === this.ocean;

                if (isClickOnWater) {
                    const playerPosition = this.playerModel.position;
                    const distanceToClickPoint = playerPosition.distanceTo(clickPoint);
                    const maxFishingDistance = 80;

                    if (distanceToClickPoint <= maxFishingDistance) {
                        this.isCasting = true;
                        this.isFishing = true;
                        this.castingStartTime = this.clock.getElapsedTime() * 1000;
                        this.network.sendCastLine(clickPoint);
                    } else {
                        // Too far — walk toward water, find shore point
                        const px = this.playerModel.position.x;
                        const pz = this.playerModel.position.z;
                        const toWaterX = clickPoint.x - px;
                        const toWaterZ = clickPoint.z - pz;
                        const toWaterLen = Math.sqrt(toWaterX * toWaterX + toWaterZ * toWaterZ);
                        const stepX = toWaterX / toWaterLen;
                        const stepZ = toWaterZ / toWaterLen;
                        // March from player toward click, stop just before water
                        let shoreX = px, shoreZ = pz;
                        for (let s = 0; s < toWaterLen; s += 2) {
                            const nx = px + stepX * s;
                            const nz = pz + stepZ * s;
                            if (this.getTerrainHeight(nx, nz) <= 0.3) break;
                            shoreX = nx;
                            shoreZ = nz;
                        }
                        const shoreY = this.getTerrainHeight(shoreX, shoreZ) + 2;
                        const shorePoint = new THREE.Vector3(shoreX, shoreY, shoreZ);

                        // Store pending cast target — will fire after arriving
                        this._pendingCastTarget = clickPoint.clone();
                        this.isFishing = false;
                        this.startMovement(shorePoint);
                    }
                }
            } else if (isDoubleTap && isOnIsland) {
                // Movement action — clear any pending cast
                this._pendingCastTarget = null;
                clickPoint.y = this.getTerrainHeight(clickPoint.x, clickPoint.z) + 2;
                this.isFishing = false;

                if (this.playerModel) {
                    this.startMovement(clickPoint);
                }
            }
        }
    }

    showNotification(message, type = 'system') {
        const area = document.getElementById('notification-area');
        if (!area) return;

        const toast = document.createElement('div');
        toast.className = `notification-toast toast-${type}`;
        toast.textContent = message;

        // Dismiss on click/touch
        toast.addEventListener('click', () => this._dismissToast(toast));

        area.appendChild(toast);

        // Max 3 toasts
        while (area.children.length > 3) {
            area.removeChild(area.firstChild);
        }

        // Auto-dismiss after 5 seconds
        setTimeout(() => this._dismissToast(toast), 5000);
    }

    _dismissToast(toast) {
        if (!toast.parentNode) return;
        toast.classList.add('dismissing');
        toast.addEventListener('animationend', () => {
            if (toast.parentNode) toast.parentNode.removeChild(toast);
        });
    }

    addGameLog(message, type = 'system') {
        // Route non-chat messages to notification toasts
        if (type !== 'chat') {
            this.showNotification(message, type);
            return;
        }
        // Chat messages go to the chat panel
        this.addChatMessage(null, message);
    }

    addChatMessage(name, text) {
        const msgs = document.getElementById('chat-messages');
        if (!msgs) return;

        const entry = document.createElement('div');
        entry.className = 'chat-msg';
        if (name) {
            entry.innerHTML = `<span class="chat-name">${name}:</span> <span class="chat-text">${text}</span>`;
        } else {
            entry.innerHTML = `<span class="chat-text">${text}</span>`;
        }
        msgs.appendChild(entry);

        // Keep last 50 messages
        while (msgs.children.length > 50) {
            msgs.removeChild(msgs.firstChild);
        }

        msgs.scrollTop = msgs.scrollHeight;

        // Show unread dot if chat is minimized
        if (!this.chatOpen) {
            const dot = document.getElementById('chat-unread-dot');
            if (dot) dot.style.display = 'block';
        }
    }

    startMovement(targetPoint) {
        const cp = this.playerModel ? this.playerModel.position : null;
        console.log(`[move] START client=(${cp ? cp.x.toFixed(2) : '?'}, ${cp ? cp.z.toFixed(2) : '?'}) target=(${targetPoint.x.toFixed(2)}, ${targetPoint.z.toFixed(2)})`);

        this.targetPosition = targetPoint;
        this.isMoving = true;
        this.moveSpeed = this.maxMoveSpeed;
        this.localMoveTicks = 0;
        this.previousDirections = [];  // reset oscillation detector for new movement
        this.stuckCounter = 0;

        this.removeFishingLine(this.auth.playerUUID);

        // Local prediction: start walk animation immediately
        if (this.playerModel && this.playerModel.walkAction) {
            this.playerModel.walkAction.play();
            if (this.playerModel.idleAction) this.playerModel.idleAction.stop();
        }

        // Tell server where we want to go (include current position for sync) — returns the move sequence number
        this.currentMoveSeq = this.network.sendMoveTo(targetPoint, cp);
    }

    stopMovement() {
        const cp = this.playerModel ? this.playerModel.position : null;
        console.log(`[move] STOP client=(${cp ? cp.x.toFixed(2) : '?'}, ${cp ? cp.z.toFixed(2) : '?'}) seq=${this.currentMoveSeq} localTicks=${this.localMoveTicks}`);

        this.isMoving = false;
        this.targetPosition = null;
        this.lastPosition = null;
        this.moveSpeed = 0;
        this.stuckCounter = 0;
        this.previousDirections = [];

        if (this.playerModel) {
            if (this.playerModel.walkAction) { this.playerModel.walkAction.stop(); this.playerModel.walkAction.reset(); }
            if (this.playerModel.idleAction) this.playerModel.idleAction.play();
        }

        // Fire pending fishing cast after arriving at shore
        if (this._pendingCastTarget) {
            const target = this._pendingCastTarget;
            this._pendingCastTarget = null;
            this.isCasting = true;
            this.isFishing = true;
            this.castingStartTime = this.clock.getElapsedTime() * 1000;
            this.network.sendCastLine(target);
        }
    }

    updateMovement() {
        if (!this.isMoving || !this.targetPosition || !this.playerModel) {
            return;
        }

        const currentPos = this.playerModel.position;
        const targetPos = this.targetPosition;
        
        // Calculate direction and distance
        const direction = new THREE.Vector3();
        direction.subVectors(targetPos, currentPos);
        direction.y = 0; // Keep movement on the XZ plane
        const distance = direction.length();
        
        // Debug log with more precision
        this.log(`Current position: (${currentPos.x.toFixed(4)}, ${currentPos.z.toFixed(4)}), Target: (${targetPos.x.toFixed(4)}, ${targetPos.z.toFixed(4)}), Distance: ${distance.toFixed(4)}`);
        
        // Check if positions are very close (even if distance calculation has issues)
        const xDiff = Math.abs(targetPos.x - currentPos.x);
        const zDiff = Math.abs(targetPos.z - currentPos.z);
        
        // Check if we've reached the destination or if positions are very close
        if (distance < this.minMoveDistance || (xDiff < 0.01 && zDiff < 0.01)) {
            this.log('Reached destination, stopping movement');
            this.log(`Final xDiff: ${xDiff.toFixed(4)}, zDiff: ${zDiff.toFixed(4)}`);
            currentPos.x = targetPos.x;
            currentPos.z = targetPos.z;
            // Update Y position based on terrain height
            currentPos.y = this.getTerrainHeight(currentPos.x, currentPos.z) + 2;
            this.moveSpeed = 0; // Set speed to zero
            this.stopMovement();
            return;
        }
        
        // Check for oscillation (character moving back and forth)
        if (this.previousDirections.length >= 2) {
            const currentDir = new THREE.Vector3().copy(direction).normalize();
            const previousDir = this.previousDirections[this.previousDirections.length - 1];
            
            // Calculate dot product to check if directions are opposite
            const dotProduct = currentDir.dot(previousDir);
            
            // If directions are nearly opposite (dot product close to -1), character is oscillating
            if (dotProduct < -0.8) {
                this.log('Detected oscillation, stopping movement');
                currentPos.copy(targetPos); // Just teleport to target
                this.moveSpeed = 0;
                this.stopMovement();
                return;
            }
        }
        
        // Check for character being stuck at same position
        if (this.lastPosition) {
            const movedDistance = new THREE.Vector3().subVectors(currentPos, this.lastPosition).length();
            if (movedDistance < 0.001) {
                this.stuckCounter++;
                this.log(`Possibly stuck: ${this.stuckCounter}/${this.maxStuckCount}`);
                
                if (this.stuckCounter >= this.maxStuckCount) {
                    this.log('Character is stuck, stopping movement');
                    this.moveSpeed = 0;
                    this.stopMovement();
                    return;
                }
            } else {
                this.stuckCounter = 0;
            }
        }
        
        // Normalize direction for movement
        direction.normalize();
        
        // Store current direction for oscillation detection (limit to last 3 directions)
        this.previousDirections.push(new THREE.Vector3().copy(direction));
        if (this.previousDirections.length > 3) {
            this.previousDirections.shift();
        }
        
        // Update last position
        this.lastPosition = currentPos.clone();
        
        // Calculate speed based on distance to target (slow down as we approach)
        let currentSpeed = this.moveSpeed;
        if (distance < this.slowdownDistance) {
            // Linearly reduce speed as we get closer to target
            currentSpeed = this.moveSpeed * (distance / this.slowdownDistance);
            // Ensure minimum speed
            currentSpeed = Math.max(currentSpeed, 0.05);
            this.log(`Slowing down: speed = ${currentSpeed.toFixed(4)}`);
        }
        
        // Inside fixed timestep loop, always use fixedTimeStep (not deltaTime)
        // currentSpeed is already per-frame at 60fps, so this is a no-op at 60fps
        // but correctly scales if fixedTimeStep changes
        currentSpeed *= 1.0; // fixedTimeStep / fixedTimeStep = 1
        
        // Calculate new position
        const newX = currentPos.x + direction.x * currentSpeed;
        const newZ = currentPos.z + direction.z * currentSpeed;
        
        // Check if new position would still be on the island
        const newPosition = new THREE.Vector3(newX, currentPos.y, newZ);
        if (this.isPointInIsland(newPosition)) {
            // Update position
            currentPos.x = newX;
            currentPos.z = newZ;

            // Update Y position based on terrain height
            currentPos.y = this.getTerrainHeight(currentPos.x, currentPos.z) + 2;

            // Update rotation to face movement direction
            const angle = Math.atan2(direction.x, direction.z);
            this.playerModel.rotation.y = angle;

        } else {
            this.log('Cannot move off island, stopping movement');
            this.moveSpeed = 0; // Set speed to zero
            this.stopMovement();
            return;
        }

        // Count this tick for server reconciliation
        this.localMoveTicks++;
    }

    getTerrainHeight(x, z) {
        const { gridSize, islandSize, heights } = this.mapData;

        // Convert world coords to grid coords and clamp
        const gx = Math.max(0, Math.min(gridSize - 1, (x / islandSize + 0.5) * (gridSize - 1)));
        const gz = Math.max(0, Math.min(gridSize - 1, (z / islandSize + 0.5) * (gridSize - 1)));

        const ix = Math.max(0, Math.min(gridSize - 2, Math.floor(gx)));
        const iz = Math.max(0, Math.min(gridSize - 2, Math.floor(gz)));
        const fx = gx - ix;
        const fz = gz - iz;

        // Bilinear interpolation
        const h00 = heights[iz * gridSize + ix];
        const h10 = heights[iz * gridSize + ix + 1];
        const h01 = heights[(iz + 1) * gridSize + ix];
        const h11 = heights[(iz + 1) * gridSize + ix + 1];

        const h0 = h00 + (h10 - h00) * fx;
        const h1 = h01 + (h11 - h01) * fx;

        return h0 + (h1 - h0) * fz;
    }

    isPointInIsland(point) {
        // Use heightmap: land where terrain height > 0.3 (water covers 0→0.3 transition zone)
        return this.getTerrainHeight(point.x, point.z) > 0.3;
    }

    updateCamera() {
        // Cinematic camera during fishing minigame — focused on the player
        if (this.minigameActive && this.playerModel) {
            const pp = this.playerModel.position;
            // Camera positioned close to the player, low angle
            const angle = this.cameraAngle + Math.PI; // behind the player
            const camDist = 22;
            const targetX = pp.x + Math.cos(angle) * camDist;
            const targetZ = pp.z + Math.sin(angle) * camDist;
            const targetY = pp.y + 10;

            // Smooth lerp to cinematic position
            const f = Math.min(1.0, 3.0 * this.deltaTime);
            this.camera.position.x += (targetX - this.camera.position.x) * f;
            this.camera.position.y += (targetY - this.camera.position.y) * f;
            this.camera.position.z += (targetZ - this.camera.position.z) * f;
            this.camera.lookAt(pp.x, pp.y + 1, pp.z);
            return;
        }

        const CAMERA_DISTANCE = 30;
        const CAMERA_HEIGHT = 25;
        const CAMERA_LOOK_Y = 2;

        const zoomFactor = Math.pow(2, this.cameraZoom);
        const dist = CAMERA_DISTANCE * zoomFactor;
        const height = CAMERA_HEIGHT * zoomFactor;

        let px = 0, pz = 0;
        if (this.playerModel) {
            px = this.playerModel.position.x;
            pz = this.playerModel.position.z;
        }

        // Camera deadzone: only move the camera target when the player
        // leaves a small rectangle around the current focus point.
        // This absorbs small reconciliation corrections and direction changes.
        const DEADZONE = 2.0; // half-size of the deadzone box
        const ct = this._cameraTarget;
        const cdx = px - ct.x;
        const cdz = pz - ct.z;
        if (Math.abs(cdx) > DEADZONE) {
            ct.x += (cdx - Math.sign(cdx) * DEADZONE);
        }
        if (Math.abs(cdz) > DEADZONE) {
            ct.z += (cdz - Math.sign(cdz) * DEADZONE);
        }
        // Smooth the camera target movement
        const camLerp = Math.min(1.0, 6.0 * this.deltaTime);
        ct.x += (px - ct.x) * camLerp * 0.3;
        ct.z += (pz - ct.z) * camLerp * 0.3;

        const x = ct.x + Math.sin(this.cameraAngle) * dist;
        const z = ct.z + Math.cos(this.cameraAngle) * dist;
        this.camera.position.set(x, height, z);
        this.camera.lookAt(ct.x, CAMERA_LOOK_Y, ct.z);
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));
        
        // Calculate delta time for framerate independence
        const currentTime = this.clock.getElapsedTime();
        this.deltaTime = Math.min(currentTime - this.lastTime, 0.1);
        this.lastTime = currentTime;

        // Update shader uniforms
        if (this.waterUniforms) this.waterUniforms.time.value = currentTime;
        if (this.grassUniforms) this.grassUniforms.time.value = currentTime;
        
        // Accumulate time for fixed timestep updates
        this.timeAccumulator += this.deltaTime;

        // Update FPS counter and track performance
        this.updateFpsCounter();

        // Fixed time step for physics — cap to 3 ticks per frame to prevent
        // burst movement on mobile after frame hitches
        const maxTicksPerFrame = 3;
        let ticksThisFrame = 0;
        while (this.timeAccumulator >= this.fixedTimeStep && ticksThisFrame < maxTicksPerFrame) {
            if (this.isMoving) {
                this.updateMovement();
            }
            this.updateFishingLines();
            this.timeAccumulator -= this.fixedTimeStep;
            ticksThisFrame++;
        }
        // Discard leftover accumulator to prevent spiral of death
        if (this.timeAccumulator > this.fixedTimeStep * maxTicksPerFrame) {
            this.timeAccumulator = 0;
        }

        // Apply smooth reconciliation correction (lerp toward _smoothTarget)
        if (this._smoothTarget && this.playerModel) {
            const st = this._smoothTarget;
            const pp = this.playerModel.position;
            const dx = st.x - pp.x;
            const dz = st.z - pp.z;
            const rem = Math.sqrt(dx * dx + dz * dz);
            if (rem < 0.05) {
                pp.x = st.x;
                pp.z = st.z;
                pp.y = this.getTerrainHeight(pp.x, pp.z) + 2;
                this._smoothTarget = null;
            } else {
                const f = Math.min(1.0, 8.0 * this.deltaTime); // fast but smooth
                pp.x += dx * f;
                pp.z += dz * f;
                pp.y = this.getTerrainHeight(pp.x, pp.z) + 2;
            }
        }

        // Update fishing minigame
        if (this.minigameActive) {
            this.updateMinigame(this.deltaTime);
        }

        // Apply camera inertia and update orbit camera
        if (!this.minigameActive) {
            if (Math.abs(this.cameraVelocity) > 0.0001) {
                this.cameraAngle -= this.cameraVelocity;
                this.cameraVelocity *= 0.95;
            } else {
                this.cameraVelocity = 0;
            }
        }
        this.updateCamera();

        // Player rotation is set by movement direction or server updates — not tied to camera
        
        // Update animation mixers for the player with deltaTime
        if (this.playerModel && this.playerModel.mixer) {
            this.playerModel.mixer.update(this.deltaTime);
        }
        
        // Interpolate remote players using buffered server snapshots
        // Render position = where the server said the player was interpDelay ms ago
        // Adaptive: scales with measured ping + jitter so buffer doesn't run dry
        const interpDelay = this.network.interpDelay;
        const renderTime = performance.now() - interpDelay;

        // Keep more snapshots when latency is high
        const maxSnapshots = interpDelay > 150 ? 20 : 10;

        for (const [playerUUID, playerModel] of this.players) {
            if (playerModel && playerModel.mixer) {
                playerModel.mixer.update(this.deltaTime);
            }

            const buf = playerModel ? playerModel.posBuffer : null;
            if (buf && buf.length >= 2) {
                // Find the two snapshots surrounding renderTime
                let i = 0;
                for (; i < buf.length - 1; i++) {
                    if (buf[i + 1].t >= renderTime) break;
                }
                const a = buf[i];
                const b = buf[Math.min(i + 1, buf.length - 1)];
                const span = b.t - a.t;
                const t = span > 0 ? Math.max(0, Math.min(1, (renderTime - a.t) / span)) : 1;

                playerModel.position.x = a.x + (b.x - a.x) * t;
                playerModel.position.y = a.y + (b.y - a.y) * t;
                playerModel.position.z = a.z + (b.z - a.z) * t;

                // Shortest-path rotation interpolation (deltaTime-scaled)
                let targetRot = a.r + (b.r - a.r) * t;
                let dr = targetRot - playerModel.rotation.y;
                if (dr > Math.PI) dr -= Math.PI * 2;
                if (dr < -Math.PI) dr += Math.PI * 2;
                playerModel.rotation.y += dr * Math.min(1.0, 10.0 * this.deltaTime);

                // Prune old snapshots we've passed (keep at least 2)
                while (buf.length > 2 && buf[1].t < renderTime) buf.shift();
            } else if (buf && buf.length === 1) {
                // Only one snapshot — lerp toward it
                const s = buf[0];
                const f = Math.min(1.0, 5.0 * this.deltaTime);
                playerModel.position.x += (s.x - playerModel.position.x) * f;
                playerModel.position.y += (s.y - playerModel.position.y) * f;
                playerModel.position.z += (s.z - playerModel.position.z) * f;
            }

            // Enforce maxSnapshots buffer size
            if (buf && buf.length > maxSnapshots) {
                buf.splice(0, buf.length - maxSnapshots);
            }

            // Bob on water
            if (playerModel && playerModel.model) {
                const height = this.getTerrainHeight(playerModel.position.x, playerModel.position.z);
                if (height < 0) {
                    const bobAmount = Math.sin((currentTime * 3 + playerUUID.charCodeAt(0) * 0.1)) * 0.1;
                    playerModel.position.y = bobAmount - 0.5;
                }
            }
        }

        // Server reconciliation happens in game_tick handler — no per-frame drift correction needed
        
        this.renderer.render(this.scene, this.camera);
        
        // Update chat bubbles (remove expired ones)
        this.updateChatBubbles();
    }

    createIsland() {
        const { gridSize, islandSize, heights } = this.mapData;

        // Build a PlaneGeometry covering the full grid, then sculpt it with the heightmap.
        const geo = new THREE.PlaneGeometry(islandSize, islandSize, gridSize - 1, gridSize - 1);
        geo.rotateX(-Math.PI / 2);

        const positions = geo.attributes.position.array;

        // Apply heightmap — clamp underwater cells to just below water line
        for (let j = 0; j < gridSize; j++) {
            for (let i = 0; i < gridSize; i++) {
                const idx = (j * gridSize + i) * 3;
                const h = heights[j * gridSize + i];
                positions[idx + 1] = h > 0.3 ? h : -2.0;
            }
        }
        geo.attributes.position.needsUpdate = true;
        geo.computeVertexNormals();

        // Vertex colors: cartoonish tropical palette
        const colors = new Float32Array(positions.length);
        const lushGreen = new THREE.Color(0x4cb840);
        const brightGreen = new THREE.Color(0x6dd854);
        const hilltopGreen = new THREE.Color(0x88e668);
        const sandColor = new THREE.Color(0xf0d890);
        const darkSand = new THREE.Color(0xd4be70);
        const wetSand = new THREE.Color(0xc4a860);

        for (let j = 0; j < gridSize; j++) {
            for (let i = 0; i < gridSize; i++) {
                const idx = (j * gridSize + i) * 3;
                const h = heights[j * gridSize + i];
                const x = positions[idx];
                const z = positions[idx + 2];

                let c;
                if (h <= 0.3) {
                    // Underwater — dark wet sand (hidden under water)
                    c = wetSand.clone();
                } else if (h < 0.7) {
                    // Beach sand zone (narrow strip)
                    const bt = Math.min(1, (h - 0.3) / 0.4);
                    c = darkSand.clone().lerp(sandColor, bt);
                } else if (h < 1.2) {
                    // Sand to grass transition
                    const gt = (h - 0.7) / 0.5;
                    c = sandColor.clone().lerp(lushGreen, gt);
                } else {
                    // Grass with height variation
                    const heightNorm = Math.min(1, (h - 1.2) / 4.0);
                    const noise = 0.5 + 0.15 * Math.sin(x * 0.25 + z * 0.18) * Math.cos(x * 0.12 - z * 0.22);
                    const blend = heightNorm + noise * 0.15;
                    if (blend < 0.4) {
                        c = lushGreen.clone().lerp(brightGreen, blend / 0.4);
                    } else {
                        c = brightGreen.clone().lerp(hilltopGreen, Math.min(1, (blend - 0.4) / 0.6));
                    }
                }

                colors[idx] = c.r;
                colors[idx + 1] = c.g;
                colors[idx + 2] = c.b;
            }
        }
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            roughness: 0.75,
            metalness: 0.0,
            flatShading: false
        });

        this.island = new THREE.Mesh(geo, material);
        this.island.receiveShadow = true;

        this.scene.add(this.island);
    }

    createGrass() {
        const islandRadius = this.islandSize / 2;

        // Dense clumps of tall pointed grass blades (folio-2025 style)
        const clumpCount = 1400;
        const bladesPerClump = 45;
        const maxBlades = clumpCount * bladesPerClump;
        const clumpRadius = 3.5;  // tighter clumps

        // Blade geometry: tall pointed triangle
        const bladeGeo = new THREE.BufferGeometry();
        const bladeVerts = new Float32Array([
            -0.5, 0, 0,
             0.5, 0, 0,
             0,   1, 0
        ]);
        bladeGeo.setAttribute('position', new THREE.BufferAttribute(bladeVerts, 3));

        const instanceGeo = new THREE.InstancedBufferGeometry();
        instanceGeo.attributes.position = bladeGeo.attributes.position;

        const instancePositions = new Float32Array(maxBlades * 3);
        const instanceRandoms = new Float32Array(maxBlades);

        let seed = 54321;
        const rand = () => {
            seed = (seed * 16807 + 0) % 2147483647;
            return (seed - 1) / 2147483646;
        };

        let idx = 0;
        let clumpsPlaced = 0;
        let attempts = 0;

        while (clumpsPlaced < clumpCount && attempts < clumpCount * 4) {
            attempts++;

            const angle = rand() * Math.PI * 2;
            const r = Math.sqrt(rand()) * islandRadius * 0.95;
            const cx = Math.cos(angle) * r;
            const cz = Math.sin(angle) * r;
            const ch = this.getTerrainHeight(cx, cz);
            if (ch < 0.5) continue; // only place grass on actual land

            for (let b = 0; b < bladesPerClump; b++) {
                const ba = rand() * Math.PI * 2;
                // Tighter distribution near center of clump (Gaussian-ish)
                // Gaussian-ish: most blades near center
                const br = rand() * rand() * clumpRadius;
                const bx = cx + Math.cos(ba) * br;
                const bz = cz + Math.sin(ba) * br;
                const bh = this.getTerrainHeight(bx, bz);
                if (bh < 0.2) continue;

                instancePositions[idx * 3] = bx;
                instancePositions[idx * 3 + 1] = bh;
                instancePositions[idx * 3 + 2] = bz;
                instanceRandoms[idx] = rand();
                idx++;
            }
            clumpsPlaced++;
        }

        instanceGeo.instanceCount = idx;
        instanceGeo.setAttribute('instancePosition',
            new THREE.InstancedBufferAttribute(instancePositions.slice(0, idx * 3), 3));
        instanceGeo.setAttribute('instanceRandom',
            new THREE.InstancedBufferAttribute(instanceRandoms.slice(0, idx), 1));

        this.grassUniforms = {
            time: { value: 0 },
            bladeHeight: { value: 4.5 },
            bladeWidth: { value: 0.9 }
        };

        const grassMat = new THREE.ShaderMaterial({
            vertexShader: GrassShader.vertexShader,
            fragmentShader: GrassShader.fragmentShader,
            uniforms: this.grassUniforms,
            side: THREE.DoubleSide
        });

        const grassMesh = new THREE.Mesh(instanceGeo, grassMat);
        grassMesh.frustumCulled = false;
        this.scene.add(grassMesh);
    }
    

    showChatBubble(playerUUID, playerName, message) {
        const playerModel = playerUUID === this.auth.playerUUID ? this.playerModel : this.players.get(playerUUID);
        if (!playerModel) return;

        // Remove any existing chat bubble for this player
        this.removeChatBubble(playerUUID);
        
        // Create a canvas for the chat bubble
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 160; // Increased height to accommodate pointer
        const context = canvas.getContext('2d');
        
        // Draw the background
        context.fillStyle = 'rgba(255, 255, 255, 0.9)'; // Increased opacity
        context.strokeStyle = 'rgba(0, 0, 0, 0.8)';
        context.lineWidth = 3;
        
        // Rounded rectangle for the bubble (excluding the bottom triangle area)
        const cornerRadius = 15;
        const triangleHeight = 20;
        const triangleWidth = 30;
        const triangleX = canvas.width / 2;
        
        // Draw the main bubble
        context.beginPath();
        context.moveTo(cornerRadius, 0);
        context.lineTo(canvas.width - cornerRadius, 0);
        context.quadraticCurveTo(canvas.width, 0, canvas.width, cornerRadius);
        context.lineTo(canvas.width, canvas.height - cornerRadius - triangleHeight);
        context.quadraticCurveTo(canvas.width, canvas.height - triangleHeight, canvas.width - cornerRadius, canvas.height - triangleHeight);
        context.lineTo(triangleX + triangleWidth / 2, canvas.height - triangleHeight);
        
        // Draw the pointer triangle
        context.lineTo(triangleX, canvas.height);
        context.lineTo(triangleX - triangleWidth / 2, canvas.height - triangleHeight);
        
        // Continue the bubble outline
        context.lineTo(cornerRadius, canvas.height - triangleHeight);
        context.quadraticCurveTo(0, canvas.height - triangleHeight, 0, canvas.height - cornerRadius - triangleHeight);
        context.lineTo(0, cornerRadius);
        context.quadraticCurveTo(0, 0, cornerRadius, 0);
        context.closePath();
        context.fill();
        context.stroke();
        
        // Draw the message text with word wrapping
        context.font = 'bold 26px Arial'; // Bolder and slightly larger text
        context.fillStyle = 'black';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        
        // Word wrap and render the message
        const maxWidth = canvas.width - 40; // Padding on both sides
        const maxLines = 3; // Maximum 3 lines
        const lineHeight = 35;
        
        // Word wrap function
        const wrapText = (text, maxWidth) => {
            const words = text.split(' ');
            let lines = [];
            let currentLine = '';
            
            for (let i = 0; i < words.length; i++) {
                const testLine = currentLine + (currentLine ? ' ' : '') + words[i];
                const metrics = context.measureText(testLine);
                
                if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = words[i];
                    
                    // Break if we've reached max lines
                    if (lines.length >= maxLines - 1) {
                        // If this is the last allowed line and there are more words,
                        // add ellipsis
                        if (i < words.length - 1) {
                            currentLine += '...';
                            break;
                        }
                    }
                } else {
                    currentLine = testLine;
                }
            }
            
            // Add the last line
            if (currentLine) {
                lines.push(currentLine);
            }
            
            return lines;
        };
        
        const lines = wrapText(message, maxWidth);
        const startY = (canvas.height - triangleHeight) / 2 - (lines.length - 1) * lineHeight / 2;
        
        lines.forEach((line, index) => {
            context.fillText(line, canvas.width / 2, startY + index * lineHeight);
        });
        
        // Create texture and sprite for the bubble
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const spriteMaterial = new THREE.SpriteMaterial({ 
            map: texture,
            transparent: true
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.y = 9;  // Position above the character name
        sprite.scale.set(15, 5, 1); // Adjusted scale for new dimensions
        playerModel.add(sprite);
        
        // Store the chat bubble with timestamp
        this.chatBubbles.set(playerUUID, {
            sprite: sprite,
            timestamp: Date.now()
        });
        
        // If this is the local player's message, also show it as a notification
        if (playerUUID === this.auth.playerUUID) {
            this.showNotification(`You: ${message.substring(0, 30)}${message.length > 30 ? '...' : ''}`);
        }
    }
    
    removeChatBubble(playerUUID) {
        if (this.chatBubbles.has(playerUUID)) {
            const bubble = this.chatBubbles.get(playerUUID);
            const playerModel = playerUUID === this.auth.playerUUID ? this.playerModel : this.players.get(playerUUID);
            
            if (playerModel && bubble.sprite) {
                playerModel.remove(bubble.sprite);
            }
            
            this.chatBubbles.delete(playerUUID);
        }
    }
    
    updateChatBubbles() {
        const currentTime = Date.now();
        const bubbleDuration = 5000; // 5 seconds
        
        for (const [playerUUID, bubble] of this.chatBubbles.entries()) {
            if (currentTime - bubble.timestamp > bubbleDuration) {
                this.removeChatBubble(playerUUID);
            }
        }
    }

    createChatUI() {
        this.chatInput = document.getElementById('chat-input');
        this.chatButton = document.getElementById('chat-button');
        this.sendButton = document.getElementById('send-button');
        this.chatPanel = document.getElementById('chat-panel');
        this.chatOpen = false;

        this.chatButton.addEventListener('click', () => this.toggleChatInput());
        this.sendButton.addEventListener('click', () => this.sendChatMessage());
        this.chatInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.sendChatMessage();
            } else if (e.key === 'Escape') {
                this.toggleChatInput(false);
            }
        });
    }

    toggleChatInput(show) {
        const shouldShow = show !== undefined ? show : !this.chatOpen;

        if (shouldShow) {
            this.chatPanel.style.display = 'flex';
            this.chatButton.innerHTML = 'Chat &#9660;';
            this.chatInput.focus();
            this.chatInputActive = true;
            this.chatOpen = true;
            // Clear unread indicator
            const dot = document.getElementById('chat-unread-dot');
            if (dot) dot.style.display = 'none';
            // Scroll to bottom
            const msgs = document.getElementById('chat-messages');
            if (msgs) msgs.scrollTop = msgs.scrollHeight;
        } else {
            this.chatPanel.style.display = 'none';
            this.chatButton.innerHTML = 'Chat &#9650;';
            this.chatInput.blur();
            this.chatInput.value = '';
            this.chatInputActive = false;
            this.chatOpen = false;
        }
    }

    sendChatMessage() {
        const message = this.chatInput.value.trim();
        if (message && this.network.isConnected) {
            const trimmedMessage = message.substring(0, 200);
            this.network.sendChatMessage(trimmedMessage);
            this.chatInput.value = '';
        }
    }
}

// Start the game when the page loads
window.addEventListener('load', () => {
    new Game();
}); 